import { isValidEmail, isValidPhone } from '@/utils'
import { Metadata } from './metadata'

export interface Warehouse {
  id: string
  company: string
  email: string
  firstName: string
  lastName: string
  addressLine1: string
  addressLine2: string
  city: string
  postcode: string
  stateProvince: string
  stateProvinceCode: string
  country: string
  countryCode: string
  phone: string
  // Integration annotations, e.g. the warehouse's id in the ecommerce platform.
  metadata: Metadata
}

// Metadata left out keeps the stored value; an empty object clears it.
export type WarehouseUpdate = Omit<Warehouse, 'metadata'> & {
  metadata?: Metadata
}

export interface RoutingRule {
  warehouseId: string
  whens: string[]
}

// Overrides are keyed by ecommerce id; a store without one follows the group's rules.
export interface GroupRouting {
  rules: RoutingRule[]
  overrides: Record<string, RoutingRule[]>
}

export type WarehouseField = Exclude<keyof Warehouse, 'id' | 'metadata'>

export const NEW_WAREHOUSE: Warehouse = {
  id: '',
  company: '',
  email: '',
  firstName: '',
  lastName: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  postcode: '',
  stateProvince: '',
  stateProvinceCode: '',
  country: '',
  countryCode: '',
  phone: '',
  metadata: {}
}

// Mirrors settings-api's save validation, so the form explains a refusal before sending.
export const REQUIRED_FIELDS: WarehouseField[] = [
  'company',
  'email',
  'firstName',
  'addressLine1',
  'city',
  'postcode',
  'stateProvince',
  'country',
  'countryCode',
  'phone'
]

export type FieldProblem = 'required' | 'invalid'

// Phones copied from contacts apps carry invisible marks (U+202A, NBSP) that fail the + check.
export const normalizePhone = (phone: string) =>
  phone.replace(/[\s\p{Cf}]/gu, '')

export const warehouseProblems = (
  warehouse: Warehouse
): Partial<Record<WarehouseField, FieldProblem>> => {
  const problems: Partial<Record<WarehouseField, FieldProblem>> = {}
  for (const field of REQUIRED_FIELDS) {
    if (!warehouse[field].trim()) problems[field] = 'required'
  }
  const phone = normalizePhone(warehouse.phone)
  const invalid: Partial<Record<WarehouseField, boolean>> = {
    email: !isValidEmail(warehouse.email),
    phone: !phone.startsWith('+') || !isValidPhone(phone),
    countryCode: warehouse.countryCode.trim().length !== 2,
    postcode:
      warehouse.countryCode.trim().toUpperCase() === 'ES' &&
      !/^[0-9]{4,5}$/.test(warehouse.postcode)
  }
  for (const [field, isInvalid] of Object.entries(invalid)) {
    const key = field as WarehouseField
    if (isInvalid && !problems[key]) problems[key] = 'invalid'
  }
  return problems
}

// Settings-api treats a rule without conditions as always true, like an explicit `true`.
export const alwaysMatches = (rule: RoutingRule) =>
  rule.whens.length === 0 || rule.whens.every((when) => when.trim() === 'true')

export const hasCatchAll = (rules: RoutingRule[]) => rules.some(alwaysMatches)

export const defaultWarehouseId = (rules: RoutingRule[]) =>
  rules.find(alwaysMatches)?.warehouseId

export const effectiveRules = (routing: GroupRouting, ecommerceId: string) =>
  routing.overrides[ecommerceId] ?? routing.rules

export const overridingStores = (routing: GroupRouting, stores: string[]) =>
  stores.filter((store) => routing.overrides[store])

export const inheritingStores = (routing: GroupRouting, stores: string[]) =>
  stores.filter((store) => !routing.overrides[store])

export const usesWarehouse = (rules: RoutingRule[], warehouseId: string) =>
  rules.some((rule) => rule.warehouseId === warehouseId)

// Settings-api refuses to delete a warehouse any of these rules still route to.
export const storesUsing = (
  routing: GroupRouting,
  stores: string[],
  warehouseId: string
) =>
  stores.filter((store) =>
    usesWarehouse(effectiveRules(routing, store), warehouseId)
  ).length

// One line for small print: "Via Roma 1, 20862 Arcore, Italy".
export const shortAddress = (w: Warehouse) =>
  [
    w.addressLine1,
    [w.postcode, w.city].filter(Boolean).join(' '),
    w.country || w.countryCode
  ]
    .filter(Boolean)
    .join(', ')

export const matchesSearch = (warehouse: Warehouse, query: string) => {
  const term = query.trim().toLowerCase()
  if (!term) return true
  return [
    warehouse.id,
    warehouse.company,
    warehouse.city,
    warehouse.country,
    warehouse.countryCode,
    warehouse.postcode
  ].some((value) => value.toLowerCase().includes(term))
}
